import React, { useEffect, useState } from 'react';
import Image from '../../atoms/image';
import type { MyCouponCardProps } from './my-coupon-card.types';
import styles from './my-coupon-card.module.css';
import Text from '../../atoms/text';
import Button from '../../atoms/button';
import Divider from '../../atoms/divider';
import { useMyCouponCard } from './use-my-coupon-card';
import Fragment from '../fragment';
import { DEFAULT_COUPON_IMAGE } from './my-coupon-card.constants';
const MyCouponCard: React.FC<MyCouponCardProps> = ({
    isAdding = false,
    status = false,
    coupon,
    expiredLabel = 'Expires',
    labelButton = 'Details',
    codeLabel,
    hasShowProgress = true,
    onPressShowMore,
    onPressGetCode,
    variant = 'default',
    className,
    testID = 'my-coupon-card',
    titleProps,
    messageProps,
    salesRuleProps,
    progressSectionProps,
    progressTrackProps,
    progressFillProps,
    progressLabel,
}) => {
    const {
        name,
        tag,
        type,
        salesrule,
        imageUri,
        isDetail,
        progressValue,
        formatDate,
        rootClassName,
        contentClassName,
        imageWrapperClassName,
        imageClassName,
        detailsClassName,
        contentBottomClassName,
        titleClassName,
        messageClassName,
        salesRuleClassName,
        progressSectionClassName,
        progressTrackClassName,
        progressFillClassName,
        progressLabelClassName,
        getCodeButtonClassName,
        typeClassName,
    } = useMyCouponCard({
        coupon,
        variant,
        className,
        titleProps,
        messageProps,
        salesRuleProps,
        progressSectionProps,
        progressTrackProps,
        progressFillProps,
        isAdding,
    });
    const [imageSrc, setImageSrc] = useState(imageUri || DEFAULT_COUPON_IMAGE);

    useEffect(() => {
        setImageSrc(imageUri || DEFAULT_COUPON_IMAGE);
    }, [imageUri]);

    const handleImageError = () => {
        if (imageSrc !== DEFAULT_COUPON_IMAGE) {
            setImageSrc(DEFAULT_COUPON_IMAGE);
        }
    };

    const handleGetCode = () => {
        onPressGetCode?.(coupon);
    };
    return (
        <div data-testid={testID} className={rootClassName}>
            <div className={contentClassName}>
                <div className={imageWrapperClassName}>
                    <Image
                        objectFit="contain"
                        src={imageSrc}
                        alt={name || 'Voucher'}
                        className={imageClassName}
                        data-testid={`${testID}-image`}
                        onError={handleImageError}
                    />
                </div>

                {isDetail && <Divider className={styles.detailDivider} orientation="vertical" thickness={1} />}

                <div className={detailsClassName}>
                    <div className={styles.headerCard}>
                        <div className={styles.headerTitle}>
                            <Text showIf={!!name} className={titleClassName} {...titleProps}>
                                {name}
                            </Text>
                        </div>
                        <div className={styles.headerButton}>
                            <Button
                                loading={isAdding}
                                disabled={status || isAdding}
                                showIf={!!onPressGetCode}
                                onClick={handleGetCode}
                                className={getCodeButtonClassName}
                                data-testid={`${testID}-details`}
                            >
                                {codeLabel}
                            </Button>
                        </div>
                    </div>

                    <Text
                        showIf={!!tag}
                        className={messageClassName}
                        {...messageProps}
                        dangerouslySetInnerHTML={{ __html: tag || '' }}
                    />

                    <Text showIf={!!type} className={typeClassName} {...titleProps}>
                        {type}
                    </Text>

                    {hasShowProgress && salesrule && (
                        <div {...progressSectionProps} className={progressSectionClassName}>
                            <div {...progressTrackProps} className={progressTrackClassName}>
                                <div
                                    {...progressFillProps}
                                    className={progressFillClassName}
                                    style={{
                                        width: `${progressValue}%`,
                                        ...progressFillProps?.style,
                                    }}
                                />
                                <Text showIf={!isDetail && !!progressLabel} className={progressLabelClassName}>
                                    {progressLabel}
                                </Text>
                            </div>
                        </div>
                    )}

                    <div className={contentBottomClassName}>
                        {isDetail ? (
                            <Fragment>
                                <Text showIf={!!hasShowProgress && !!salesrule} className={progressLabelClassName}>
                                    {progressLabel}
                                </Text>

                                <Text showIf={!!salesrule} className={salesRuleClassName} {...salesRuleProps}>
                                    {expiredLabel}: {formatDate(salesrule?.to_date)}
                                </Text>
                            </Fragment>
                        ) : (
                            <Fragment>
                                <Text showIf={!!salesrule} className={salesRuleClassName} {...salesRuleProps}>
                                    {expiredLabel}: {formatDate(salesrule?.to_date)}
                                </Text>

                                <Button
                                    showIf={!!onPressShowMore}
                                    onClick={onPressShowMore}
                                    className={styles.defaultDetailsButton}
                                    data-testid={`${testID}-details`}
                                >
                                    {labelButton}
                                </Button>
                            </Fragment>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MyCouponCard;
